import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ArrowLeftRight, Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Props {
  classes: any[];
  onSuccess: () => void;
}

export default function TransferStudentDialog({ classes, onSuccess }: Props) {
  const [open, setOpen] = useState(false);
  const [fromClassId, setFromClassId] = useState("");
  const [toClassId, setToClassId] = useState("");
  const [childId, setChildId] = useState("");
  const [loading, setLoading] = useState(false);
  const { profile } = useAuth();
  const { toast } = useToast();

  const { data: students } = useQuery({
    queryKey: ["transfer-students", fromClassId],
    queryFn: async () => {
      if (!fromClassId) return [];
      const { data } = await supabase.from("children").select("id, full_name").eq("class_id", fromClassId);
      return data || [];
    },
    enabled: !!fromClassId && open,
  });

  const handleTransfer = async () => {
    if (!childId || !toClassId) return;
    setLoading(true);
    const { error } = await supabase.from("children").update({ class_id: toClassId }).eq("id", childId);
    setLoading(false);
    if (error) {
      toast({ title: "خطأ", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "تم نقل الطالب بنجاح ✅" });
    setOpen(false);
    setFromClassId(""); setToClassId(""); setChildId("");
    onSuccess();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <ArrowLeftRight className="w-4 h-4" />
          نقل طالب
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" dir="rtl">
        <DialogHeader>
          <DialogTitle>نقل طالب بين الفصول</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>من الفصل</Label>
            <Select value={fromClassId} onValueChange={(v) => { setFromClassId(v); setChildId(""); }}>
              <SelectTrigger><SelectValue placeholder="اختر الفصل المصدر" /></SelectTrigger>
              <SelectContent>
                {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {fromClassId && (
            <div>
              <Label>الطالب</Label>
              <Select value={childId} onValueChange={setChildId}>
                <SelectTrigger><SelectValue placeholder="اختر الطالب" /></SelectTrigger>
                <SelectContent>
                  {(students || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label>إلى الفصل</Label>
            <Select value={toClassId} onValueChange={setToClassId}>
              <SelectTrigger><SelectValue placeholder="اختر الفصل الهدف" /></SelectTrigger>
              <SelectContent>
                {classes.filter((c) => c.id !== fromClassId).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <Button onClick={handleTransfer} disabled={!childId || !toClassId || loading} className="w-full">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "نقل الطالب"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
